import { useQuery } from '@tanstack/react-query';
import type { Json } from '@pazportal/supabase';
import { supabase } from '../lib/supabase';
import { EM_DASH } from '../lib/formatters';

export const LOCATION_DEVICE_COLUMNS =
  'id, name, device_type, status, ip_address, mac_address, firmware_version, software_version, vendor_name, make_model, serial_number, last_seen_at, availability_pct, availability_from, availability_to, updated_at';

export type LocationDevice = {
  id: string;
  name: string | null;
  device_type: string | null;
  status: string | null;
  ip_address: string | null;
  mac_address: string | null;
  firmware_version: string | null;
  software_version: string | null;
  vendor_name: string | null;
  make_model: string | null;
  serial_number: string | null;
  last_seen_at: string | null;
  availability_pct: number | null;
  availability_from: string | null;
  availability_to: string | null;
  updated_at: string | null;
};

export function formatAvailabilityPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return EM_DASH;
  return `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}%`;
}

export function useLocationDevices(locationId: string | undefined) {
  return useQuery({
    queryKey: ['location-devices', locationId],
    queryFn: async () => {
      if (!locationId) return [];
      const { data, error } = await supabase
        .from('devices')
        .select(LOCATION_DEVICE_COLUMNS)
        .eq('location_id', locationId)
        .order('name');
      if (error) throw error;
      return (data ?? []) as LocationDevice[];
    },
    enabled: Boolean(locationId),
    staleTime: 2 * 60 * 1000,
  });
}

export type NetworkAlert = {
  id: string;
  severity: string | null;
  status: string | null;
  alert_name: string | null;
  message: string | null;
  detected_on: string | null;
  entity_name: string | null;
  device_id: string | null;
};

export type DeviceInterface = {
  id: string;
  device_id: string | null;
  name: string | null;
  interface_type: string | null;
  operational_status: string | null;
  admin_status: string | null;
  ip_address: string | null;
  negotiated_speed: string | null;
  duplex: string | null;
};

export type UnifiWlan = {
  id: string;
  name: string | null;
  enabled: boolean | null;
  security: string | null;
  hidden: boolean | null;
  updated_at: string | null;
};

export type UnifiClient = {
  id: string;
  name: string | null;
  ip_address: string | null;
  mac_address: string | null;
  connection_type: string | null;
  ssid: string | null;
  connected_at: string | null;
};

export type UnifiAccessPoint = {
  id: string;
  device_id: string | null;
  name: string | null;
  model: string | null;
  mac_address: string | null;
  ip_address: string | null;
  firmware_version: string | null;
};

export function useLocationNetworkLive(locationId: string | undefined) {
  return useQuery({
    queryKey: ['onsite-network-live', locationId],
    queryFn: async () => {
      if (!locationId) {
        return { alerts: [], interfaces: [], wlans: [], clients: [], accessPoints: [] };
      }
      const [alerts, interfaces, wlans, clients, accessPoints] = await Promise.all([
        supabase
          .from('network_alerts')
          .select('id, severity, status, alert_name, message, detected_on, entity_name, device_id')
          .eq('location_id', locationId)
          .or('status.is.null,and(status.neq.cleared,status.neq.resolved)')
          .order('detected_on', { ascending: false })
          .limit(25),
        supabase
          .from('device_interfaces')
          .select('id, device_id, name, interface_type, operational_status, admin_status, ip_address, negotiated_speed, duplex')
          .eq('location_id', locationId)
          .order('name')
          .limit(80),
        supabase
          .from('unifi_wlans')
          .select('id, name, enabled, security, hidden, updated_at')
          .eq('location_id', locationId)
          .order('name'),
        supabase
          .from('unifi_clients')
          .select('id, name, ip_address, mac_address, connection_type, ssid, connected_at')
          .eq('location_id', locationId)
          .order('name'),
        supabase
          .from('unifi_access_points')
          .select('id, device_id, name, model, mac_address, ip_address, firmware_version')
          .eq('location_id', locationId)
          .order('name'),
      ]);
      if (alerts.error) throw alerts.error;
      if (interfaces.error) throw interfaces.error;
      if (wlans.error) throw wlans.error;
      if (clients.error) throw clients.error;
      if (accessPoints.error) throw accessPoints.error;
      return {
        alerts: (alerts.data ?? []) as unknown as NetworkAlert[],
        interfaces: (interfaces.data ?? []) as unknown as DeviceInterface[],
        wlans: (wlans.data ?? []) as unknown as UnifiWlan[],
        clients: (clients.data ?? []) as unknown as UnifiClient[],
        accessPoints: (accessPoints.data ?? []) as unknown as UnifiAccessPoint[],
      };
    },
    enabled: Boolean(locationId),
    staleTime: 2 * 60 * 1000,
  });
}

// ─── UniFi Site Manager (the live network feed) ─────────────────────────────

export type UnifiSiteSummary = {
  id: string | null;
  site_name: string | null;
  unifi_host_name: string | null;
  total_devices: number | null;
  offline_devices: number | null;
  wifi_client_count: number | null;
  wired_client_count: number | null;
  ssid_count: number | null;
  wan_links: number | null;
  pending_update_devices: number | null;
  critical_notifications: number | null;
  updated_at: string | null;
};

export const UNIFI_SITE_COLUMNS =
  'id, site_name, unifi_host_name, total_devices, offline_devices, wifi_client_count, wired_client_count, ssid_count, wan_links, pending_update_devices, critical_notifications, updated_at';

/** UniFi sites mapped to this location (via property_directory → hierarchy.property_code). */
export function useLocationUnifiSites(locationId: string | undefined) {
  return useQuery({
    queryKey: ['unifi-sites', locationId],
    enabled: Boolean(locationId),
    queryFn: async () => {
      if (!locationId) return [];
      const { data, error } = await supabase
        .from('unifi_sites_customer_view')
        .select(UNIFI_SITE_COLUMNS)
        .eq('location_id', locationId)
        .order('site_name');
      if (error) throw error;
      return (data ?? []) as UnifiSiteSummary[];
    },
    staleTime: 60 * 1000,
  });
}

// ─── Integration status (staff-only RPC) ────────────────────────────────────

export type IntegrationState =
  | 'live'
  | 'stale'
  | 'offline'
  | 'not_configured'
  | 'unmapped'
  | 'never_synced'
  | 'last_run_failed'
  | 'never_run'
  | 'unknown';

export interface IntegrationStatus {
  auvik: {
    state: IntegrationState;
    devices: number | null;
    devicesLatestAt: string | null;
    lastPollAt: string | null;
  };
  unifi: {
    state: IntegrationState;
    sites: number | null;
    mappedSites: number | null;
    sitesLatestAt: string | null;
    lastPollAt: string | null;
    lastError: string | null;
  };
}

function asRecord(value: Json | undefined): Record<string, Json | undefined> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function asState(value: Json | undefined): IntegrationState {
  const known: IntegrationState[] = [
    'live', 'stale', 'offline', 'not_configured', 'unmapped', 'never_synced', 'last_run_failed', 'never_run',
  ];
  return typeof value === 'string' && (known as string[]).includes(value) ? (value as IntegrationState) : 'unknown';
}

function asNumber(value: Json | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asString(value: Json | undefined): string | null {
  return typeof value === 'string' && value ? value : null;
}

export function parseIntegrationStatus(raw: Json | null): IntegrationStatus | null {
  const root = asRecord(raw ?? undefined);
  if (!root) return null;
  const auvik = asRecord(root.auvik) ?? {};
  const unifi = asRecord(root.unifi) ?? {};
  return {
    auvik: {
      state: asState(auvik.state),
      devices: asNumber(auvik.devices),
      devicesLatestAt: asString(auvik.devices_latest_at),
      lastPollAt: asString(auvik.last_poll_at),
    },
    unifi: {
      state: asState(unifi.state),
      sites: asNumber(unifi.sites),
      mappedSites: asNumber(unifi.mapped_sites),
      sitesLatestAt: asString(unifi.sites_latest_at),
      lastPollAt: asString(unifi.last_poll_at),
      lastError: asString(unifi.last_error),
    },
  };
}

/**
 * integration_status_snapshot() is staff-only. A technician signed in with a
 * trip_scope claim but no staff role gets an RPC error; callers treat `null`
 * as "connection state unknown" and word empty states accordingly.
 */
export function useIntegrationStatus() {
  return useQuery({
    queryKey: ['integration-status'],
    queryFn: async (): Promise<IntegrationStatus | null> => {
      const { data, error } = await supabase.rpc('integration_status_snapshot');
      if (error) return null;
      return parseIntegrationStatus((data ?? null) as Json | null);
    },
    retry: false,
    staleTime: 60 * 1000,
  });
}

export function describeIntegrationState(name: string, state: IntegrationState | undefined): string {
  switch (state) {
    case 'live':
      return `${name} is connected and reporting.`;
    case 'stale':
      return `${name} is connected but the last update is more than 10 minutes old.`;
    case 'offline':
      return `${name} has not reported in over 30 minutes.`;
    case 'not_configured':
      return `${name} is not connected. No credentials are configured.`;
    case 'unmapped':
      return `${name} is connected but no tenants are mapped to customers yet.`;
    case 'never_synced':
      return `${name} is configured but has not completed a sync yet.`;
    case 'last_run_failed':
      return `${name}'s last poll failed.`;
    case 'never_run':
      return `${name} has never run.`;
    default:
      return `${name} connection state is unavailable for this account.`;
  }
}
